import { type SerializedStyles, css } from '@emotion/react';
import { Box, Divider, NavLink, Text } from '@inflearn/ds-react';
import { KoOnly, useLang } from '@inflearn/i18n-tools';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import type { ReactNode } from 'react';
import React from 'react';

import { isNil } from 'lodash-es';
import { INFLEARN_URL, MY_URL, USERS_URL } from '../../../constants/urls';
import { useCurrentUrl } from '../../../hooks/useCurrentUrl';
import useClientAccountProfileQuery from '../../../queries/useClientAccountProfileQuery';
import { eventNames } from '../../../utils/GTM/eventNames';
import { useTrackMixpanelEvent } from '../../../utils/GTM/useTrackMixpanelEvent';
import { getB2BRole } from '../../../utils/getB2BRole';
import { B2BMenus } from './B2BMenus';
import { GameButton } from './GameButton';
import { PartnersMenus } from './PartnersMenus';

export default function LNB() {
  const { t } = useTranslation();
  const { isKo } = useLang();
  const { trackMixpanelEvent } = useTrackMixpanelEvent();
  const { data: user } = useClientAccountProfileQuery();
  const { isB2BUser } = getB2BRole(user ?? null);
  const isPartner = !isNil(user?.marketingPartner);

  return (
    <Box component="nav" aria-label={t('마이 페이지')} pos="relative">
      <KoOnly>
        <B2BMenus />
      </KoOnly>
      {isPartner && <PartnersMenus />}
      {(isB2BUser || isPartner) && <Divider my={16} />}
      <LinkCategoryTitle>HOME</LinkCategoryTitle>
      <NavLinkList>
        <NavLink
          component={Link}
          label={t('대시보드')}
          href={
            isNil(user)
              ? USERS_URL.MY_DASHBOARD()
              : USERS_URL.DASHBOARD({ userId: String(user.id) })
          }
        />
        <KoOnly>
          <NavLink component="a" label={t('알림')} href={INFLEARN_URL.MESSAGES()} />
        </KoOnly>
      </NavLinkList>
      <LinkCategoryTitle>{t('학습 관리')}</LinkCategoryTitle>
      <NavLinkList
        pageMetadata={[
          {
            mainPage: MY_URL.COURSES(),
            subPages: [
              MY_URL.LIST(),
              MY_URL.COURSES(),
              MY_URL.ACTIVITY_LOGS(),
              MY_URL.VOUCHERS(),
              MY_URL.CERTIFICATES(),
              MY_URL.ARCHIVED_COURSES(),
            ],
          },
          {
            mainPage: MY_URL.QUESTION(),
            subPages: [MY_URL.CHAT(), MY_URL.STUDY(), MY_URL.PROJECT()],
          },
        ]}
      >
        <NavLink
          label={t('내 학습')}
          component={Link}
          href={MY_URL.COURSES()}
          onClick={() => {
            trackMixpanelEvent(eventNames.CLICK_MY_COURSES, {
              entry_point: 'LNB',
            });
          }}
        />
        <KoOnly>
          <NavLink component="a" label={t('내 로드맵')} href={INFLEARN_URL.MY_ROADMAPS()} />
        </KoOnly>
        <NavLink component={Link} label={t('강의 노트')} href={MY_URL.NOTES()} />
        <NavLink
          component={Link}
          label={t('멘토링')}
          href={MY_URL.MENTORINGS()}
          /** @description: <KoOnly> 태그를 래핑하는 경우
           * 아래 active 로직은 child만 확인하고 있기 때문에 KoOnly 자식의 NavLink는 건드리지 못함
           * 관련 로직을 추가하면 너무 복잡해져서 간단하게 css로 처리
           */
          css={{ display: isKo ? 'block' : 'none' }}
        />
        <NavLink component={Link} label={t('커뮤니티 게시글')} href={MY_URL.QUESTION()} />
        <KoOnly>
          <NavLink component="a" label={t('수강전 문의')} href={INFLEARN_URL.MY_INQUIRIES()} />
        </KoOnly>
      </NavLinkList>
      <LinkCategoryTitle>{t('수강신청 관리')}</LinkCategoryTitle>
      <NavLinkList>
        <NavLink component="a" label={t('수강바구니')} href={INFLEARN_URL.CARTS()} />
        <NavLink component={Link} label={t('좋아요')} href={MY_URL.LIKES()} />
        <KoOnly>
          <NavLink component="a" label={t('쿠폰함')} href={INFLEARN_URL.MY_COUPONS()} />
          <NavLink component="a" label={t('포인트')} href={INFLEARN_URL.MY_POINTS()} />
        </KoOnly>
        <NavLinkList
          pageMetadata={[
            {
              mainPage: MY_URL.GIFTS_RECEIVED(),
              subPages: [MY_URL.GIFTS_RECEIVED(), MY_URL.GIFTS_SENT()],
            },
          ]}
          listStyle={css({
            '&&': {
              marginBottom: '0px',
            },
          })}
        >
          <NavLink
            component={Link}
            label={t('마이페이지_LNB_선물함')}
            href={MY_URL.GIFTS_RECEIVED()}
          />
        </NavLinkList>
        <NavLink component={Link} label={t('구매 내역')} href={MY_URL.ORDERS()} />
      </NavLinkList>
      <LinkCategoryTitle>{t('설정')}</LinkCategoryTitle>
      <NavLinkList>
        <NavLink component={Link} label={t('계정 정보')} href={MY_URL.ACCOUNT()} />
        <NavLink component={Link} label={t('알림 설정')} href={MY_URL.NOTIFICATION()} />
        <NavLink component={Link} label={t('마이페이지_기기관리_타이틀')} href={MY_URL.DEVICE()} />
      </NavLinkList>
      <GameButton />
    </Box>
  );
}

type LinkCategoryTitleProps = {
  children: ReactNode;
};

export function LinkCategoryTitle({ children }: LinkCategoryTitleProps) {
  return (
    <Text size="xs" mb={12} color="gray.5">
      {children}
    </Text>
  );
}

type PageMetadata = {
  mainPage: string;
  subPages: string[];
};

type NavLinkListProps = {
  children: ReactNode;
  pageMetadata?: PageMetadata[];
  listStyle?: SerializedStyles;
};

export function NavLinkList({ children, pageMetadata, listStyle }: NavLinkListProps) {
  const { currentUrlWithoutQueryString } = useCurrentUrl();

  return (
    <ul>
      {React.Children.map(children, (child) => {
        if (React.isValidElement<Record<string, unknown> & { href: string }>(child)) {
          const childUrl = child.props.href;

          const currentPageMetadata = pageMetadata?.find(
            (metadata) => metadata.mainPage === childUrl,
          );

          const isActiveSubPage = currentPageMetadata?.subPages.includes(
            currentUrlWithoutQueryString,
          );
          const isActivePath = currentUrlWithoutQueryString === childUrl || isActiveSubPage;

          const navLinkProps = {
            color: 'infgreen',
            sx: { borderRadius: '4px' },
            active: isActivePath,
            'aria-current': isActivePath ? 'page' : null,
            component: isActivePath ? 'span' : child.props.component,
          };

          return (
            <li key={child.key} css={[styleLi, listStyle]}>
              {/* @NOTE: <NavLink /> */}
              {React.cloneElement(child, {
                ...child.props,
                ...navLinkProps,
              })}
            </li>
          );
        }

        return null;
      })}
    </ul>
  );
}

const styleLi = css({
  listStyleType: 'none',

  '&:last-of-type': {
    marginBottom: '8px',
  },
});
